import API from "../../utils/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { isTimesheetAdminViewEmployee, shouldIncludeSavedTimesheetsInList } from "../../utils/timesheetAccess";

/**
 * POST timesheet/getAllTimesheet params.
 * include_saved: true for regular employees/managers only (not admin or allowlisted all-view employees).
 * For employees, scopes the list to the logged-in user when employee_id is omitted
 * (except allowlisted employees who see all timesheets like admin).
 */
function mergeGetAllTimesheetParams(params = {}) {
  if (typeof localStorage === "undefined") return params;
  const role = localStorage.getItem("user_role");
  const userId = localStorage.getItem("user_id");
  const out = { ...params };
  if (out.include_saved === undefined) {
    if (shouldIncludeSavedTimesheetsInList()) {
      out.include_saved = true;
    }
  }
  if (
    role === "employee" &&
    !isTimesheetAdminViewEmployee(userId) &&
    userId != null &&
    userId !== "" &&
    (out.employee_id === undefined ||
      out.employee_id === null ||
      out.employee_id === "")
  ) {
    out.employee_id = userId;
  }
  return out;
}

const insertUpdateTimesheet = async (state) => {
  try {
    const { params, insertUpdateFlag, timesheet_id } = state;

    const API_NAME =
      insertUpdateFlag === 0
        ? "timesheet/createTimesheet"
        : `timesheet/updateTimesheet/${timesheet_id}`;
    
    // Match the exact pattern of other working timesheet APIs
    // The interceptor already sets x-access-token, user-type, and user-id headers
    if (insertUpdateFlag === 0) {
      // Create - use POST (same pattern as getAllTimesheet which works)
      const data = await API.post(`${API_NAME}`, params);
      return data;
    } else {
      // Update - use PUT (same pattern as rejectTimesheet and getTimesheetDetails which work)
      const data = await API.put(`${API_NAME}`, params);
      return data;
    }
  } catch (error) {
    console.error("Error in insertUpdateTimesheet:", error);
    return [];
  }
};

const getAllTimesheets = createAsyncThunk(
  "timesheet/getAllTimesheetsStatus",
  async (params = {}) => {
    try {
      const {
        data: { data },
      } = await API.post(
        `timesheet/getAllTimesheet`,
        mergeGetAllTimesheetParams(params),
      );
      return data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }
);

/** Aggregated hours per employee for Reports → Timesheet tab */
const getTimesheetReport = createAsyncThunk(
  "timesheet/getTimesheetReportStatus",
  async (params = {}) => {
    try {
      const {
        data: { data },
      } = await API.post(
        `timesheet/getTimesheetReport`,
        mergeGetAllTimesheetParams(params),
      );
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.log(error);
      return [];
    }
  },
);

/**
 * Single request returning timesheets with embedded itemDetails (Reports tab fallback).
 * Backend: POST timesheet/getTimesheetDetailsBulk — same filters as getAllTimesheet.
 */
const getTimesheetDetailsBulk = createAsyncThunk(
  "timesheet/getTimesheetDetailsBulkStatus",
  async (params = {}) => {
    try {
      const {
        data: { data },
      } = await API.post(
        `timesheet/getTimesheetDetailsBulk`,
        mergeGetAllTimesheetParams(params),
      );
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.log(error);
      return [];
    }
  },
);

const approveTimesheet = async (timesheet_id) => {
  try {
    const response = await API.get(
      `timesheet/approveTimesheet/${timesheet_id}`
    );
    if (response.status === 200)
      return { status: true, message: "Timesheet approved successfully" };
    else return { status: false, message: response.message };
  } catch (error) {
    return { status: false, message: error.message };
  }
};

const rejectTimesheet = async (params) => {
  try {
    const response = await API.put(`timesheet/rejectTimesheet`, params);
    if (response.status === 200)
      return { status: true, message: "Timesheet rejected successfully" };
    else return { status: false, message: response.message };
  } catch (error) {
    return { status: false, message: error.message };
  }
};

const getTimesheetEntries = async (params = {}) => {
  try {
    const response = await API.put(`timesheet/getTimesheetDetails`, params);
    if (response.status === 200) return response.data.data;
    else return response.message;
  } catch (error) {
    console.log(error);
    return error.message;
  }
};

const getTimesheetHolidays = async ({
  start_date,
  end_date,
  employee_id,
} = {}) => {
  try {
    const response = await API.get("timesheet/holidays", {
      params: { start_date, end_date, employee_id },
    });
    const data = response?.data?.data;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Error loading timesheet holidays:", error);
    return [];
  }
};

const deleteTimesheet = async (timesheet_id) => {
  try {
    const response = await API.delete(`timesheet/deleteTimesheet/${timesheet_id}`);
    console.log("Delete timesheet response:", response);
    if (response.status === 200 && response.data && response.data.status === 1) {
      return { status: true, message: response.data.message || "Timesheet deleted successfully" };
    } else {
      return { status: false, message: response.data?.message || "Error deleting timesheet" };
    }
  } catch (error) {
    console.error("Error deleting timesheet:", error);
    console.error("Error response:", error.response);
    // Handle 404 and other errors
    if (error.response) {
      const errorMessage = error.response.data?.message || error.response.statusText || "Error deleting timesheet";
      return { status: false, message: errorMessage };
    }
    return { status: false, message: error.message || "Error deleting timesheet" };
  }
};

export {
  insertUpdateTimesheet,
  getAllTimesheets,
  getTimesheetReport,
  getTimesheetDetailsBulk,
  approveTimesheet,
  rejectTimesheet,
  getTimesheetEntries,
  getTimesheetHolidays,
  deleteTimesheet,
};
